import React, { useState, useEffect, useRef, useCallback } from 'react';
import { io, Socket } from 'socket.io-client';
// import QRCode from 'react-qr-code'; // Likely unused now
import {
    Sidebar,
    MessageList,
    Message,
    MessageInput,
    ConversationList,
    Conversation,
    Avatar,
    ConversationHeader,
    MessageSeparator,
    TypingIndicator,
    InfoButton,
    MessageModel,
    AttachmentButton,
    SendButton
} from '@chatscope/chat-ui-kit-react';
import {
    PaperClipIcon, PlayCircleIcon, DocumentTextIcon, MusicalNoteIcon,
    PhoneIcon, MapPinIcon, UserIcon, InformationCircleIcon, TrashIcon, PhoneXMarkIcon,
    CheckIcon, CheckCircleIcon
} from '@heroicons/react/24/outline';
import { Chat, WhatsAppMessage, MediaToSend, PresenceUpdateData, PLACEHOLDER_AVATAR_SVG, SendMessagePayload, VCardContact } from './types';
import { formatLastSeen, isDifferentDay, getMessagePosition, parseVCard, formatPhoneNumber, createVCard, getContactName } from './utils';
import {
    applyMessageAck,
    getSendStatusFromAck,
    markPendingMessagesUnknown,
    mergeMessageSendState,
    reconcileSendSuccess
} from './messageSendState';
import { getNextUnreadCount } from './chatUnreadState';
import QRCodeDisplay from './components/QRCodeDisplay'; // Import new component
import ConnectionStatus from './components/ConnectionStatus'; // Import new component
import { Link } from 'react-router-dom'; // Import Link for navigation
import { UserCircleIcon } from '@heroicons/react/24/solid'; // Import solid icon for known clients
import ChatSidebar from './components/ChatSidebar';
import ChatWindow from './components/ChatWindow';
import AddChatModal from './components/AddChatModal'; // Import the new modal
import ReplyPreview from './components/ReplyPreview'; // Revert: Remove .tsx extension
import axiosInstance from '../../api/axiosConfig';
import { useSettings } from '../../contexts/SettingsContext';
import { ToastContainer, toast } from 'react-toastify'; // Import react-toastify
import 'react-toastify/dist/ReactToastify.css'; // Import default CSS
// Removed unused 'r' import
import { Bars3Icon } from '@heroicons/react/24/outline'; // Import for toggle button

// Helper function to convert VAPID key (needs to be defined outside component or imported)
function urlBase64ToUint8Array(base64String: string): Uint8Array {
    const padding = '='.repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding)
        .replace(/-/g, '+')
        .replace(/_/g, '/');

    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);

    for (let i = 0; i < rawData.length; ++i) {
        outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
}


// Rely solely on the environment variable for the socket URL
const SOCKET_SERVER_URL = process.env.REACT_APP_SOCKET_URL || "/";
const DEFAULT_HISTORY_MESSAGES_LIMIT = 30;
const STALLED_OUTGOING_MESSAGE_MS = 2 * 60 * 1000;
const STALLED_OUTGOING_MESSAGE_ERROR = 'Подтверждение не получено. Обновите чат перед повторной отправкой.';
const SEND_SOCKET_DISCONNECTED_ERROR = 'Нет соединения с сервером. Сообщение не отправлено.';
const SEND_INTERRUPTED_MESSAGE_ERROR = 'Соединение прервалось во время отправки. Проверьте историю чата перед повторной отправкой.';

const getRecoveredSendState = (msgData: any, timestampMs: number): Pick<WhatsAppMessage, 'sendStatus' | 'sendError'> => {
    if (!msgData?.fromMe) {
        return {};
    }

    const ack = Number(msgData.ack);
    if (Number.isFinite(ack) && ack < 0) {
        return {
            sendStatus: 'failed',
            sendError: 'WhatsApp отклонил отправку сообщения.'
        };
    }

    if (ack === 0 && Date.now() - timestampMs >= STALLED_OUTGOING_MESSAGE_MS) {
        return {
            sendStatus: 'unknown',
            sendError: STALLED_OUTGOING_MESSAGE_ERROR
        };
    }

    return {
        sendStatus: getSendStatusFromAck(msgData.ack)
    };
};

const markStalledOutgoingMessage = (message: WhatsAppMessage, now: number): WhatsAppMessage => {
    const timestamp = Number(message.timestamp);
    const isStalledOutgoing =
        message.direction === 'outgoing' &&
        message.sendStatus === 'pending' &&
        (message.ack === 0 || message.ack === undefined) &&
        Number.isFinite(timestamp) &&
        now - timestamp >= STALLED_OUTGOING_MESSAGE_MS;

    if (!isStalledOutgoing) {
        return message;
    }

    return {
        ...message,
        sendStatus: 'unknown',
        sendError: message.sendError || STALLED_OUTGOING_MESSAGE_ERROR
    };
};

const markStalledOutgoingMessages = (
    prevMessages: { [chatId: string]: WhatsAppMessage[] },
    now: number
): { [chatId: string]: WhatsAppMessage[] } => {
    let hasChanges = false;

    const nextMessages = Object.entries(prevMessages).reduce<{ [chatId: string]: WhatsAppMessage[] }>((acc, [chatId, chatMessages]) => {
        const updatedChatMessages = chatMessages.map(message => markStalledOutgoingMessage(message, now));
        const chatChanged = updatedChatMessages.some((message, index) => message !== chatMessages[index]);

        if (chatChanged) {
            hasChanges = true;
        }

        acc[chatId] = chatChanged ? updatedChatMessages : chatMessages;
        return acc;
    }, {});

    return hasChanges ? nextMessages : prevMessages;
};

// Extend Chat interface locally or update types.ts if preferred
interface EnrichedChat extends Chat {
    phone?: string;
    clientId?: string;
    clientIds?: string[];
    clientNames?: string[];
    isKnownClient?: boolean;
    contactName?: string;
}

// Define structure for client stats (adjust based on actual API response)
// Add name to the stats object for easier display later
interface ClientStatsWithName extends ClientStats {
    name: string;
}

// Define structure for client stats (adjust based on actual API response)
interface ClientStats {
    totalSpent: number;
    totalOrderCount: number;
    activeOrderCount: number;
    latestCompletionDate: string | null;
    totalPaid: number;
    totalOwed: number;
    quoteCount: number;
    averageOrderValue: number;
    firstOrderDate: string | null;
    latestOrderDate: string | null;
}

const WhatsAppPage: React.FC = () => {
    const [socket, setSocket] = useState<Socket | null>(null);
    const [qrCode, setQrCode] = useState<string | null>(null);
    const [isConnected, setIsConnected] = useState<boolean>(false);
    const [isAuthenticating, setIsAuthenticating] = useState<boolean>(false);
    const [chats, setChats] = useState<EnrichedChat[]>([]);
    const [activeChat, setActiveChat] = useState<Chat | null>(null);
    const [messages, setMessages] = useState<{ [chatId: string]: WhatsAppMessage[] }>({});
    const [error, setError] = useState<string | null>(null);
    const [isLoadingHistory, setIsLoadingHistory] = useState<boolean>(false);
    const [messageInputValue, setMessageInputValue] = useState("");
    const [activeChatPresence, setActiveChatPresence] = useState<string | undefined>(undefined);
    const [activeChatLastSeen, setActiveChatLastSeen] = useState<number | undefined>(undefined);
    const socketRef = useRef<Socket | null>(null);
    const pendingMessageAcksRef = useRef<Map<string, number>>(new Map());
    const fileInputRef = useRef<HTMLInputElement>(null);
    const activeChatRef = useRef(activeChat); // Ref to track activeChat in effects
    const notificationSoundRef = useRef<HTMLAudioElement | null>(null); // Ref for the audio element

    const chatsRef = useRef<EnrichedChat[]>([]);
    // Update state to expect an array of clients per phone number
    const [clientInfoMap, setClientInfoMap] = useState<{ [phone: string]: { id: string; name: string, contactName: string, brand: string, phone: string }[] }>({}); // State for phone -> [{id, name, contactName}] mapping
    // Update stats state to hold a map of clientId -> stats object (including name)
    const [activeClientStatsMap, setActiveClientStatsMap] = useState<{ [clientId: string]: ClientStatsWithName } | null>(null);
    const [isLoadingStats, setIsLoadingStats] = useState<boolean>(false);
    const { settings, loading: settingsLoading, error: settingsError } = useSettings();
    const [isMobileSidebarVisible, setIsMobileSidebarVisible] = useState(true); // State for mobile sidebar visibility
    const [isCollapsed, setIsCollapsed] = useState(false);
    const [isAddChatModalOpen, setIsAddChatModalOpen] = useState(false); // State for Add Chat modal
    const [isAddingChat, setIsAddingChat] = useState(false); // Loading state for adding chat
    const [scrollToMessageId, setScrollToMessageId] = useState<string | null>(null); // For scrolling to quoted message
    const [isLoadingAllHistoryForScroll, setIsLoadingAllHistoryForScroll] = useState<boolean>(false); // Track if loading all history

    useEffect(() => {
        const intervalId = window.setInterval(() => {
            const now = Date.now();
            setMessages(prevMessages => markStalledOutgoingMessages(prevMessages, now));
        }, 10000);

        return () => window.clearInterval(intervalId);
    }, []);

    useEffect(() => {
        if (!isConnected) {
            setIsCollapsed(false); // Ensure sidebar is expanded when chat list is received
            setIsMobileSidebarVisible(true); // Ensure sidebar is expanded when chat list is received
        }
    }, [isConnected]);

    useEffect(() => {
        chats.sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0));
    }, [JSON.stringify(chats.flatMap(c => c.timestamp))]); // Sort chats by timestamp on change

    // Effect to update document title based on settings
    useEffect(() => {
        const baseTitle = 'Система управления рекламным агентством';
        let title = baseTitle; // Default title

        if (settingsLoading) {
            title = `Загрузка... | ${baseTitle}`;
        } else if (settingsError) {
            title = `Ошибка | ${baseTitle}`;
        } else if (settings?.name && settings.name !== 'Название не задано') {
            // Prepend company name if it exists and is not the default placeholder
            title = `${settings.name} | ${baseTitle}`;
        }
        // Otherwise, keep the baseTitle

        document.title = title;

    }, [settings, settingsLoading, settingsError]);

    // Effect for Push Notification Subscription (runs once on mount)
    useEffect(() => {
        let isSubscribed = false; // Flag to track if already subscribed in this session

        const subscribeUser = async () => {
            console.log('[Push Subscription] Starting subscription process...');
            if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
                console.warn('Push messaging is not supported');
                return;
            }

            try {
                const registration = await navigator.serviceWorker.register('/service-worker.js');
                console.log('[Push Subscription] Service Worker registered with scope:', registration.scope);

                // Wait for the service worker to become active
                await navigator.serviceWorker.ready;
                console.log('[Push Subscription] Service Worker ready.');

                // Check current subscription status
                const existingSubscription = await registration.pushManager.getSubscription();
                if (existingSubscription) {
                    console.log('[Push Subscription] User IS already subscribed.');
                    isSubscribed = true;
                    // Optional: You might want to send the subscription to the backend
                    // every time the component mounts to ensure it's up-to-date,
                    // even if isSubscribed is true. The backend handles duplicates.
                    try {
                        console.log('[Push Subscription] Sending existing subscription to server (update)...');
                        await axiosInstance.post('/push/subscribe', existingSubscription);
                    } catch (subError) {
                        console.error('[Push Subscription] Error sending existing subscription to server:', subError);
                    }
                    return; // Stop here if already subscribed
                }

                console.log('[Push Subscription] User is NOT subscribed. Requesting permission...');
                // Request permission
                // Note: requestPermission() returns a Promise in modern browsers
                const permissionResult = await Notification.requestPermission();
                if (permissionResult !== 'granted') {
                    console.warn('[Push Subscription] Permission for notifications was denied.');
                    // Optionally inform the user via UI state
                    return;
                }

                console.log('[Push Subscription] Notification permission granted.');

                // Get VAPID public key from backend
                console.log('[Push Subscription] Fetching VAPID public key...');
                const response = await axiosInstance.get<string>('/push/vapidPublicKey'); // Expect string response
                const vapidPublicKey = response.data;

                if (!vapidPublicKey || typeof vapidPublicKey !== 'string') {
                    console.error('[Push Subscription] Could not get valid VAPID public key from server.');
                    // Optionally inform the user via UI state
                    return;
                }
                console.log('[Push Subscription] VAPID Public Key received.');

                const applicationServerKey = urlBase64ToUint8Array(vapidPublicKey);
                console.log('[Push Subscription] VAPID key converted.');

                // Subscribe the user
                console.log('[Push Subscription] Subscribing user...');
                const subscription = await registration.pushManager.subscribe({
                    userVisibleOnly: true, // Required for web push
                    applicationServerKey: applicationServerKey // The converted key
                });

                console.log('[Push Subscription] User subscribed:', subscription);

                // Send subscription to backend
                console.log('[Push Subscription] Sending subscription to server...');
                await axiosInstance.post('/push/subscribe', subscription);
                console.log('[Push Subscription] Subscription sent successfully.');
                isSubscribed = true;

            } catch (error) {
                console.error('[Push Subscription] Failed to subscribe the user: ', error);
                // Optionally inform the user via UI state
            }
        };

        // Call subscribeUser only once after component mounts and socket is ready (or based on another condition)
        // We might want to trigger this based on user action (e.g., button click) instead of automatically
        // For now, let's try subscribing when the component mounts if not already subscribed.
        if (!isSubscribed) {
            subscribeUser();
        }

    }, []); // Empty dependency array ensures this runs once on mount


    // Function to fetch client info (ID and name) for given phone numbers
    const fetchClientInfo = async (phoneNumbers: string[]) => {
        if (phoneNumbers.length === 0) return;
        try {
            console.log('[API Call] Fetching client info for phones:', phoneNumbers);
            // Update expected response type to match the new backend structure (array of clients)
            const response = await axiosInstance.post<{ [phone: string]: { id: string; name: string, contactName: string, brand: string, phone: string }[] }>('/clients/by-phones', { phoneNumbers });
            console.log('[API Response] Received client info:', response.data);
            setClientInfoMap(prevMap => ({ ...prevMap, ...response.data }));
        } catch (err) {
            console.error('Error fetching client info:', err);
            // Handle error appropriately
        }
    };

    // Function to fetch stats for multiple client IDs
    const fetchMultipleClientStats = async (clients: { id: string; name: string }[]) => {
        if (!clients || clients.length === 0) {
            setActiveClientStatsMap(null);
            return;
        }
        setIsLoadingStats(true);
        setActiveClientStatsMap(null); // Clear previous stats map
        const statsMap: { [clientId: string]: ClientStatsWithName } = {};
        const promises = clients.map(async (client) => {
            try {
                console.log(`[API Call] Fetching stats for client ID: ${client.id} (${client.name})`);
                const response = await axiosInstance.get<ClientStats>(`/clients/${client.id}/stats`);
                console.log(`[API Response] Received stats for ${client.id}:`, response.data);
                statsMap[client.id] = { ...response.data, name: client.name }; // Add name to the stats object
            } catch (err) {
                console.error(`Error fetching stats for client ${client.id} (${client.name}):`, err);
                // Store error or handle appropriately, maybe add an error flag to the map entry
                statsMap[client.id] = { name: client.name, error: `Failed to load stats for ${client.name}` } as any; // Add name and error indicator
            }
        });

        await Promise.all(promises); // Wait for all stats requests to complete
        console.log('[Stats Update] Final stats map:', statsMap);
        setActiveClientStatsMap(statsMap);
        setIsLoadingStats(false);
    };

    // Effect for initial socket connection and core listeners
    useEffect(() => {
        console.log('Attempting to connect to WebSocket server:', SOCKET_SERVER_URL);
        const newSocket = io(SOCKET_SERVER_URL);
        socketRef.current = newSocket;
        setSocket(newSocket);

        const handleConnectionLoss = (errorMessage: string) => {
            pendingMessageAcksRef.current.clear();
            setIsConnected(false);
            setQrCode(null);
            setActiveChat(null);
            setMessages(prevMessages => markPendingMessagesUnknown(prevMessages, SEND_INTERRUPTED_MESSAGE_ERROR));
            setChats([]);
            setError(errorMessage);
        };

        // --- Core Connection Listeners ---
        newSocket.on('connect', () => { console.log('WebSocket connected:', newSocket.id); setError(null); });
        newSocket.on('disconnect', (reason: Socket.DisconnectReason) => {
            console.log('WebSocket disconnected:', reason);
            handleConnectionLoss('Отключено от сервера. Попытка переподключения...');
        });
        newSocket.on('connect_error', (err: Error) => { console.error('WebSocket connection error:', err); setError(`Ошибка подключения: ${err.message}. Запущен ли бэкенд сервер?`); setIsConnected(false); setQrCode(null); });

        // --- Authentication Listeners ---
        newSocket.on('whatsapp_qr', (qr: string) => { console.log('QR code received'); setQrCode(qr); setIsAuthenticating(true); setIsConnected(false); setError(null); });
        newSocket.on('whatsapp_authenticated', () => { console.log('WhatsApp authenticated'); setQrCode(null); setIsAuthenticating(false); setError(null); });
        newSocket.on('whatsapp_auth_failure', (msg: string) => { console.error('WhatsApp authentication failure:', msg); setError(`Ошибка аутентификации: ${msg}. Пожалуйста, попробуйте отсканировать QR-код еще раз.`); setQrCode(null); setIsAuthenticating(false); setIsConnected(false); });
        newSocket.on('whatsapp_ready', () => { console.log('WhatsApp client ready'); setIsConnected(true); setIsAuthenticating(false); setQrCode(null); setError(null); });
        newSocket.on('whatsapp_disconnected', (reason: string) => {
            console.log('WhatsApp client disconnected:', reason);
            handleConnectionLoss(`WhatsApp отключен: ${reason}. Возможно, потребуется повторная аутентификация.`);
        });

        // --- Data Listeners ---
        newSocket.on('whatsapp_chat_list', (chatList: Chat[]) => {
            addAllChats(chatList, setChats, fetchClientInfo);
        });

        newSocket.on('whatsapp_chat_history', (data: { chatId: string; internalChatId?: string; phone?: string; messages: any[] }) => {
            console.log(`[Socket Event] Received whatsapp_chat_history for ${data.chatId} (internal: ${data.internalChatId}), ${data.messages.length} messages.`);
            const activeChatKeys = getChatMessageKeys(activeChatRef.current);
            const responseKeys = getUniqueStrings(
                data.chatId,
                data.internalChatId,
                getPhoneChatId(data.phone)
            );
            const activeChatMatchesResponse = activeChatKeys.some(key => responseKeys.includes(key));
            const messageKeys = getUniqueStrings(
                ...responseKeys,
                ...(activeChatMatchesResponse ? activeChatKeys : [])
            );
            const historyChatId = activeChatMatchesResponse && activeChatRef.current?.id
                ? activeChatRef.current.id
                : data.chatId;
            const validMessagesData = data.messages.filter(msgData => (
                msgData &&
                msgData.id &&
                msgData.timestamp &&
                !(msgData.type === 'error' && String(msgData.error || '').startsWith('Failed to process message:'))
            ));
            const historyMessages: WhatsAppMessage[] = validMessagesData.map((msgData: any): WhatsAppMessage => {
                const bufferedAck = pendingMessageAcksRef.current.get(msgData.id);
                if (bufferedAck !== undefined) {
                    pendingMessageAcksRef.current.delete(msgData.id);
                }

                return convertMessageData({
                    ...msgData,
                    ack: bufferedAck ?? msgData.ack
                }, {
                    ...data,
                    chatId: historyChatId
                });
            });
            let newMessagesForChat: WhatsAppMessage[] = [];

            setMessages(prevMessages => {
                const existingMessages = messageKeys.flatMap(key => prevMessages[key] || []);
                const combinedMessages = [...historyMessages, ...existingMessages];
                const uniqueMessagesMap = new Map<string, WhatsAppMessage>();
                combinedMessages.forEach(msg => {
                    if (!uniqueMessagesMap.has(msg.id)) {
                        uniqueMessagesMap.set(msg.id, msg);
                    }
                });
                newMessagesForChat = Array.from(uniqueMessagesMap.values());
                newMessagesForChat.sort((a, b) => a.timestamp - b.timestamp);
                
                const updated = { ...prevMessages };
                messageKeys.forEach(key => {
                    updated[key] = newMessagesForChat;
                });
                return updated;
            });

            setIsLoadingHistory(false);
            setIsLoadingAllHistoryForScroll(false);

            if (scrollToMessageId && activeChatKeys.some(key => messageKeys.includes(key))) {
                const targetMessageExists = newMessagesForChat.some(m => m.id === scrollToMessageId);
                if (targetMessageExists) {
                    console.log(`[History Loaded] Target message ${scrollToMessageId} found.`);
                } else {
                    toast.error(`Не удалось найти цитируемое сообщение в истории чата.`);
                    setScrollToMessageId(null);
                }
            }
        });

        newSocket.on('whatsapp_message', (msgData: any) => {
            console.log('[Socket Event] Received whatsapp_message:', { ...msgData, mediaData: msgData.mediaData ? '...' : null, quotedMessage: msgData.quotedMessage ? '{...}' : null });

            if (msgData?.type === 'error' && String(msgData.error || '').startsWith('Failed to process message:')) {
                console.warn('[Socket Event] Skipping technical WhatsApp message processing error:', msgData.error);
                return;
            }
            console.log('[Message Debug] chatId:', msgData.chatId);
            console.log('[Message Debug] from:', msgData.from);
            console.log('[Message Debug] phoneNumber:', msgData.phoneNumber);
            console.log('[Message Debug] chatsRef at message time:', chatsRef.current.map(c => ({ id: c.id, phone: (c as EnrichedChat).phone })));

            const rawChatId: string = msgData.chatId;
            const incomingPhone: string | undefined = msgData.phoneNumber
                || (rawChatId.includes('@c.us') ? rawChatId.split('@')[0] : undefined)
                || (msgData.cUsContactId ? msgData.cUsContactId.split('@')[0] : undefined);
            const incomingNormalizedPhone = incomingPhone ? normalizePhoneForApi(incomingPhone) : undefined;

            const existingByPhone = incomingNormalizedPhone
                ? chatsRef.current.find(c =>
                    c.id !== rawChatId &&
                    Boolean((c as EnrichedChat).phone) &&
                    normalizePhoneForApi((c as EnrichedChat).phone as string) === incomingNormalizedPhone
                )
                : undefined;

            const existingById = chatsRef.current.find(c => c.id === rawChatId);
            const existingChat = existingById || existingByPhone;
            const rawMessageKeys = getChatMessageKeys(
                existingChat || ({ id: rawChatId, phone: incomingNormalizedPhone } as EnrichedChat),
                [rawChatId, msgData.cUsContactId]
            );
            const activeChatKeys = getChatMessageKeys(activeChatRef.current);
            const messageMatchesActiveChat = activeChatKeys.some(key => rawMessageKeys.includes(key));
            const normalizedChatId = messageMatchesActiveChat && activeChatRef.current?.id
                ? activeChatRef.current.id
                : (existingChat ? existingChat.id : rawChatId);
            const messageKeys = getUniqueStrings(
                ...rawMessageKeys,
                normalizedChatId,
                ...(messageMatchesActiveChat ? activeChatKeys : [])
            );

            if (existingByPhone && !existingById && incomingNormalizedPhone) {
                const cUsId = `${incomingNormalizedPhone}@c.us`;
                console.log(`[Chat Merge] Merging @lid chat ${existingByPhone.id} → ${cUsId}`);
                setChats(prev => prev.map(c =>
                    c.id === existingByPhone.id
                        ? {
                            ...c,
                            id: cUsId,
                            aliases: getUniqueStrings(...(c.aliases || []), c.id, rawChatId, cUsId),
                            phone: incomingNormalizedPhone
                        }
                        : c
                ));
                setMessages(prev => {
                    if (prev[existingByPhone.id]) {
                        const updated = { ...prev };
                        updated[cUsId] = prev[existingByPhone.id];
                        delete updated[existingByPhone.id];
                        return updated;
                    }
                    return prev;
                });
            }

            if (normalizedChatId !== rawChatId) {
                console.log(`[Message Normalize] Remapping chatId ${rawChatId} → ${normalizedChatId}`);
            }

            console.log(`[Message Debug] rawChatId: ${rawChatId}`);
            console.log(`[Message Debug] incomingPhone: ${incomingPhone}`);
            console.log(`[Message Debug] normalizedChatId: ${normalizedChatId}`);
            console.log(`[Message Debug] chatsRef current ids:`, chatsRef.current.map(c => ({ id: c.id, phone: (c as EnrichedChat).phone })));

            const bufferedAck = pendingMessageAcksRef.current.get(msgData.id);
            if (bufferedAck !== undefined) {
                pendingMessageAcksRef.current.delete(msgData.id);
            }
            const effectiveAck = bufferedAck ?? msgData.ack;
            const messageTimestamp = msgData.timestamp * 1000;
            const newMessage: WhatsAppMessage = {
                id: msgData.id,
                message: msgData.body || '',
                sender: msgData.senderName,
                direction: msgData.fromMe ? 'outgoing' : 'incoming',
                position: 'normal',
                chatId: normalizedChatId,
                timestamp: messageTimestamp,
                profilePicUrl: msgData.profilePicUrl,
                type: msgData.type,
                hasMedia: msgData.hasMedia,
                mimetype: msgData.mimetype,
                filename: msgData.filename,
                mediaData: msgData.mediaData,
                error: msgData.error,
                location: msgData.location,
                vcard: msgData.vcard,
                ack: effectiveAck,
                mediaRequiresDownload: msgData.mediaRequiresDownload,
                hasQuotedMsg: msgData.hasQuotedMsg,
                quotedMessage: msgData.quotedMessage ? convertMessageData(msgData.quotedMessage, { chatId: normalizedChatId, messages: messages[normalizedChatId] || [] }) : msgData.quotedMessage,
                historySource: msgData.historySource,
                isArchivedOnly: msgData.isArchivedOnly,
                ...getRecoveredSendState({ ...msgData, ack: effectiveAck }, messageTimestamp),
            };
            setMessages(prevMessages => {
                const uniqueChatMessagesMap = new Map<string, WhatsAppMessage>();
                messageKeys.flatMap(key => prevMessages[key] || []).forEach(message => {
                    if (!uniqueChatMessagesMap.has(message.id)) {
                        uniqueChatMessagesMap.set(message.id, message);
                    }
                });
                const chatMessages = Array.from(uniqueChatMessagesMap.values()).sort((a, b) => a.timestamp - b.timestamp);
                
                const existingMessageIndex = chatMessages.findIndex(m => m.id === newMessage.id);
                
                if (existingMessageIndex !== -1) {
                    const updatedMessages = [...chatMessages];
                    updatedMessages[existingMessageIndex] = mergeMessageSendState(
                        updatedMessages[existingMessageIndex],
                        newMessage
                    );
                    console.log(`[Message Update] Updated existing message ${newMessage.id} in chat ${newMessage.chatId}`);
                    const updated = { ...prevMessages };
                    messageKeys.forEach(key => {
                        updated[key] = updatedMessages;
                    });
                    return updated;
                }
                
                if (newMessage.direction === 'outgoing') {
                    const tempMessageIndex = chatMessages.findIndex((m: WhatsAppMessage) => 
                        m.direction === 'outgoing' && 
                        m.id.startsWith('temp-') && 
                        (m.message === newMessage.message || (m.hasMedia && m.type === newMessage.type))
                    );
                    
                    if (tempMessageIndex !== -1) {
                        const tempMessage = chatMessages[tempMessageIndex];
                        if (tempMessage.hasMedia && tempMessage.mediaData && !newMessage.mediaData) {
                            newMessage.mediaData = tempMessage.mediaData;
                            newMessage.mimetype = tempMessage.mimetype;
                            newMessage.filename = tempMessage.filename;
                        }
                        
                        const updatedMessages = [...chatMessages];
                        updatedMessages[tempMessageIndex] = newMessage;
                        console.log(`[Message Replace] Replaced temp message with real ${newMessage.id} in chat ${newMessage.chatId}`);
                        const updated = { ...prevMessages };
                        messageKeys.forEach(key => {
                            updated[key] = updatedMessages;
                        });
                        return updated;
                    }
                }
                
                console.log(`[Message Add] Adding new message ${newMessage.id} to chat ${newMessage.chatId}`);
                const updatedMessages = [...chatMessages, newMessage];
                const updated = { ...prevMessages };
                messageKeys.forEach(key => {
                    updated[key] = updatedMessages;
                });
                return updated;
            });
            // Play sound for new incoming messages not in the active chat
            if (!newMessage.fromMe && newMessage.chatId !== activeChatRef.current?.id) {
                console.log(`Playing notification sound for message in chat ${newMessage.chatId}`);
                notificationSoundRef.current?.play().catch(e => console.error("Error playing sound:", e));
            } else if (!newMessage.fromMe && newMessage.chatId === activeChatRef.current?.id) {
                // If message is for the active chat, mark it as read immediately
                if (socketRef.current && socketRef.current.connected) {
                    console.log(`Sending mark as read for incoming message in active chat ${newMessage.chatId}`);
                    socketRef.current.emit('whatsapp_mark_chat_read_frontend', newMessage.chatId);
                }
            }

            if (!chatsRef.current.find(c =>
                c.id === normalizedChatId ||
                (incomingNormalizedPhone &&
                    Boolean((c as EnrichedChat).phone) &&
                    normalizePhoneForApi((c as EnrichedChat).phone as string) === incomingNormalizedPhone)
            )) {
                addChatIfNotExists({
                    id: newMessage.chatId,
                    name: newMessage.fromMe ? '' : (newMessage.sender || 'Unknown'),
                    profilePicUrl: newMessage.profilePicUrl || PLACEHOLDER_AVATAR_SVG,
                    lastMessage: newMessage.message,
                    unreadCount: newMessage.fromMe ? 0 : 1,
                    timestamp: newMessage.timestamp,
                }, setChats, fetchClientInfo);
            }

            setChats(prevChats => {
                let lastMessageText = newMessage.message;
                if (newMessage.hasMedia) {
                    if (newMessage.type === 'image') lastMessageText = '📷 Изображение';
                    else if (newMessage.type === 'video') lastMessageText = '📹 Видео';
                    else if (newMessage.type === 'audio' || newMessage.type === 'ptt') lastMessageText = '🎵 Аудио';
                    else if (newMessage.type === 'document') lastMessageText = `📄 ${newMessage.filename || 'Документ'}`;
                    else lastMessageText = '📎 Вложение';
                    if (newMessage.message) lastMessageText += `: ${newMessage.message}`;
                }
                else if (newMessage.type === 'location') lastMessageText = '📍 Местоположение';
                else if (newMessage.type === 'vcard') {
                    const contactName = getContactName(newMessage.vcard);
                    lastMessageText = `👤 Контакт: ${contactName}`;
                }
                else if (newMessage.type === 'call_log') lastMessageText = '📞 Журнал вызовов';
                else if (newMessage.type === 'revoked') lastMessageText = '🗑️ Сообщение удалено';
                else if (newMessage.type === 'group_notification') lastMessageText = `ℹ️ ${newMessage.message}`;

                return prevChats.map(chat => {
                    const isMatch = chat.id === normalizedChatId ||
                        (incomingNormalizedPhone &&
                            Boolean((chat as EnrichedChat).phone) &&
                            normalizePhoneForApi((chat as EnrichedChat).phone as string) === incomingNormalizedPhone);

                    if (!isMatch) return chat;

                    const effectiveChatId = chat.id;
                    const isActiveChat = effectiveChatId === activeChatRef.current?.id ||
                        normalizedChatId === activeChatRef.current?.id;
                    const newUnreadCount = getNextUnreadCount(
                        chat.unreadCount,
                        !newMessage.fromMe,
                        isActiveChat
                    );

                    return {
                        ...chat,
                        lastMessage: lastMessageText,
                        timestamp: newMessage.timestamp,
                        unreadCount: newUnreadCount
                    };
                });
            });

        });

        // --- Error Listeners ---
        newSocket.on('whatsapp_send_message_success', (data: { clientMessageId?: string; messageId?: string; ack?: number; timestamp?: number }) => {
            if (!data?.clientMessageId) return;

            const bufferedAck = data.messageId
                ? pendingMessageAcksRef.current.get(data.messageId)
                : undefined;
            if (data.messageId && bufferedAck !== undefined) {
                pendingMessageAcksRef.current.delete(data.messageId);
            }

            setMessages(prevMessages => {
                return reconcileSendSuccess(prevMessages, {
                    ...data,
                    clientMessageId: data.clientMessageId as string
                }, bufferedAck).messages;
            });
        });

        newSocket.on('whatsapp_send_message_error', (data: { clientMessageId?: string; messageId?: string; chatId?: string; error?: string; uncertain?: boolean } | string) => {
            const errorMessage = typeof data === 'string'
                ? data
                : data?.error || 'Не удалось отправить сообщение.';
            const clientMessageId = typeof data === 'string' ? undefined : data?.clientMessageId;
            const messageId = typeof data === 'string' ? undefined : data?.messageId;
            const uncertain = typeof data === 'string' ? false : Boolean(data?.uncertain);

            if (messageId) {
                pendingMessageAcksRef.current.delete(messageId);
            }

            setIsLoadingHistory(false);
            setIsLoadingAllHistoryForScroll(false);
            setIsAuthenticating(false);

            if (clientMessageId || messageId) {
                setMessages(prevMessages => {
                    let messagesUpdated = false;
                    const newMessagesState = { ...prevMessages };

                    for (const chatId in newMessagesState) {
                        const messageIndex = newMessagesState[chatId].findIndex(msg =>
                            msg.id === clientMessageId || Boolean(messageId && msg.id === messageId)
                        );

                        if (messageIndex !== -1) {
                            const updatedMessages = [...newMessagesState[chatId]];
                            updatedMessages[messageIndex] = {
                                ...updatedMessages[messageIndex],
                                ack: uncertain ? updatedMessages[messageIndex].ack : -1,
                                sendStatus: uncertain ? 'unknown' : 'failed',
                                sendError: errorMessage
                            };
                            newMessagesState[chatId] = updatedMessages;
                            messagesUpdated = true;
                        }
                    }

                    return messagesUpdated ? newMessagesState : prevMessages;
                });
            }

            toast.error(errorMessage);
        });

        newSocket.on('whatsapp_error', (errorMessage: string) => {
            console.error('[Socket Event] Received whatsapp_error:', errorMessage);
            setIsLoadingHistory(false);
            setIsLoadingAllHistoryForScroll(false);
            setIsAuthenticating(false);

            const isChatScopedError =
                errorMessage.startsWith('Error fetching chat history:') ||
                errorMessage.startsWith('Error marking chat as read:');

            const isSendScopedError =
                errorMessage.startsWith('Не удалось отправить сообщение:') ||
                errorMessage.startsWith('Не удалось отправить медиа') ||
                errorMessage.includes('WhatsApp Web limitations') ||
                errorMessage === 'Cannot send empty message or invalid media.' ||
                errorMessage === 'Chat ID is required to send message.';

            if (isChatScopedError || isSendScopedError) {
                toast.error(errorMessage);
                return;
            }

            setError(errorMessage);
        });
        newSocket.on('whatsapp_init_error', (errorMessage: string) => { console.error('[Socket Event] Received whatsapp_init_error:', errorMessage); setError(`WhatsApp Initialization Failed: ${errorMessage}. Please check backend logs.`); setIsConnected(false); setIsAuthenticating(false); setQrCode(null); });

        // Create audio element on mount
        // Use a placeholder path - replace 'notification.mp3' with your actual file name if different
        // Ensure the sound file exists in client/public/sounds/
        try {
            notificationSoundRef.current = new Audio('/sounds/whatsapp_notification.mp3');
            notificationSoundRef.current.preload = 'auto';
            console.log('Audio element created for notification sound.');
        } catch (audioError) {
            console.error("Failed to create Audio element:", audioError);
            // Handle cases where Audio constructor might fail (e.g., specific browser issues)
        }


        // --- Cleanup ---
        return () => {
            console.log('Disconnecting WebSocket...');
            newSocket.disconnect();
            socketRef.current = null;
            // Clean up audio element if needed
            if (notificationSoundRef.current) {
                notificationSoundRef.current = null; // Release reference
            }
        };
    }, []); // Empty dependency array: runs only once on mount

    // Separate useEffect for handling socket events that depend on state (messages, activeChat)
    useEffect(() => {
        if (!socket) return;

        const handleMessageAck = (data: { messageId: string; ack: number }) => {
            console.log(`[Socket Event] Received whatsapp_message_ack: ID=${data.messageId}, ACK=${data.ack}`);
            if (!data.messageId) {
                console.warn('[Socket Event] Ignoring whatsapp_message_ack without a message id.');
                return;
            }
            pendingMessageAcksRef.current.set(data.messageId, data.ack);
            if (pendingMessageAcksRef.current.size > 200) {
                const oldestMessageId = pendingMessageAcksRef.current.keys().next().value;
                if (oldestMessageId) pendingMessageAcksRef.current.delete(oldestMessageId);
            }
            setMessages(prevMessages => {
                const result = applyMessageAck(prevMessages, data.messageId, data.ack);
                if (result.matched) {
                    pendingMessageAcksRef.current.delete(data.messageId);
                    console.log(`[State Update] Updated ACK for message ${data.messageId} to ${data.ack}`);
                }
                return result.messages;
            });
        };

        const handlePresenceUpdate = (data: PresenceUpdateData) => {
            console.log(`[Socket Event] Received whatsapp_presence_update: ChatID=${data.chatId}, Status=${data.status}, LastSeen=${data.lastSeen}`);
            if (activeChatRef.current?.id === data.chatId) {
                console.log(`[State Update] Updating presence for active chat ${data.chatId} to ${data.status}`);
                setActiveChatPresence(data.status);
                setActiveChatLastSeen(data.lastSeen);
            }
        };

        const handleProfilePicUpdate = (data: { contactId: string; profilePicUrl: string | null; phone?: string }) => {
            console.log(`[Socket Event] Received whatsapp_profile_pic_update for contact ${data.contactId}`);
            setChats(prevChats =>
                prevChats.map(chat => {
                    if (chat.id === data.contactId) {
                        return {
                            ...chat,
                            profilePicUrl: data.profilePicUrl || PLACEHOLDER_AVATAR_SVG,
                            phone: (chat as EnrichedChat).phone || data.phone || undefined
                        };
                    }
                    return chat;
                })
            );
        };

        socket.on('whatsapp_message_ack', handleMessageAck);
        socket.on('whatsapp_presence_update', handlePresenceUpdate);
        socket.on('whatsapp_profile_pic_update', handleProfilePicUpdate);


        return () => {
            if (socket) {
                socket.off('whatsapp_message_ack', handleMessageAck);
                socket.off('whatsapp_presence_update', handlePresenceUpdate);
                socket.off('whatsapp_profile_pic_update', handleProfilePicUpdate);
            }
        };
    }, [socket]); // Re-run if socket instance changes


    // Effect to update chat info (name, clientId, isKnownClient) once clientInfoMap is populated/updated
    useEffect(() => {
        setChats((prevChats): EnrichedChat[] => // Ensure type consistency
            prevChats.map((chat): EnrichedChat => {
                const phone = (chat as EnrichedChat).phone; // Access the added phone property safely
                if (phone && clientInfoMap[phone] && clientInfoMap[phone].length > 0) {
                    const clientInfoArray = clientInfoMap[phone];
                    const firstClient = clientInfoArray[0];
                    const allClientIds = clientInfoArray.map(client => client.id);
                    const allClientNames = clientInfoArray.map(client => `${client.name} (${client.brand})`); // Include contact person in the name
                    const joinedDisplayNames = allClientNames.join(', '); // For display in sidebar

                    // Check if data needs updating
                    const needsUpdate = chat.name !== joinedDisplayNames ||
                        !(chat as EnrichedChat).isKnownClient ||
                        JSON.stringify((chat as EnrichedChat).clientIds) !== JSON.stringify(allClientIds);

                    if (needsUpdate) {
                        console.log(`Updating info for ${phone}: Names ${joinedDisplayNames}, IDs ${allClientIds.join(',')}`);
                        return {
                            ...chat,
                            name: joinedDisplayNames, // Display name (comma-separated)
                            contactName: firstClient.contactName, // Show first contact person
                            clientId: firstClient.id, // Keep first ID for potential primary link/action
                            clientIds: allClientIds, // Store all IDs
                            clientNames: allClientNames, // Store all names
                            isKnownClient: true // Mark as known
                        };
                    }
                }
                // Ensure isKnownClient is explicitly false if not found or array is empty
                // Also ensure clientIds and clientNames are undefined if not known
                const isKnown = !!(phone && clientInfoMap[phone] && clientInfoMap[phone].length > 0);
                return {
                    ...chat,
                    isKnownClient: isKnown,
                    clientIds: isKnown ? (chat as EnrichedChat).clientIds : undefined,
                    clientNames: isKnown ? (chat as EnrichedChat).clientNames : undefined,
                };
            })
        );
    }, [clientInfoMap]); // Re-run when the info map changes


    // Update activeChatRef and fetch stats when activeChat changes
    useEffect(() => {
        activeChatRef.current = activeChat;
    }, [activeChat]);

    useEffect(() => {
        chatsRef.current = chats;
    }, [chats])

    // Function to toggle mobile sidebar visibility
    const toggleMobileSidebarVisibility = useCallback(() => {
        setIsMobileSidebarVisible(prev => !prev);
    }, []);

    const toggleSidebar = () => setIsCollapsed(!isCollapsed);

    // --- Moved handleConversationClick definition earlier ---
    // Handle clicking on a conversation
    const handleConversationClick = useCallback((chat: EnrichedChat) => {
        console.log(`[Click] Opening chat: ${chat.name} (ID: ${chat.id}, ClientID: ${chat.clientId})`);

        // Hide sidebar on mobile when a chat is selected
        setIsMobileSidebarVisible(false);

        // Immediately update the chats state to remove unread count for the clicked chat
        setChats(prevChats => prevChats.map(c =>
            c.id === chat.id ? { ...c, unreadCount: 0 } : c
        ));
        setActiveChat(chat);
        setActiveChatPresence(undefined); // Reset presence
        setActiveChatLastSeen(undefined); // Reset last seen
        setActiveClientStatsMap(null); // Clear stats map when changing chat

        // Fetch stats for all associated clients if known
        if (chat.isKnownClient && chat.clientIds && chat.clientNames && chat.clientIds.length > 0) {
            const clientsToFetch = chat.clientIds.map((id, index) => ({ id, name: chat.clientNames![index] }));
            fetchMultipleClientStats(clientsToFetch);
        }

        // Mark chat as read on the backend
        if (socketRef.current && socketRef.current.connected) {
            console.log(`[Click] Sending mark as read for chat ${chat.id}`);
            socketRef.current.emit('whatsapp_mark_chat_read_frontend', chat.id);
            // Note: We don't necessarily need to wait for confirmation here.
            // The UI might update based on incoming message ACKs changing to 3 (read).
        }


        // Fetch message history if needed
        const existingMessages = getMessagesForChat(messages, chat);
        const historyExists = existingMessages.length > 0;
        if (!historyExists && socketRef.current && socketRef.current.connected) {
            console.log(`[Click] Requesting history via socket for ${chat.id} (no messages in state)`);
            setIsLoadingHistory(true);
            socketRef.current.emit('whatsapp_get_chat_history', getChatHistoryRequest(chat, DEFAULT_HISTORY_MESSAGES_LIMIT));
        } else if (historyExists) {
            console.log(`[Click] History already exists for ${chat.id}, ${existingMessages.length} messages`);
            setIsLoadingHistory(false);
        } else {
            console.warn(`[Click] Cannot request history - socket not connected`);
            setIsLoadingHistory(false);
        }
    }, [messages]); // Depend on messages state
    // --- End of moved handleConversationClick ---


    // Effect for Add Chat by Phone Number listeners
    useEffect(() => {
        if (!socket) return;

        const handleAddChatSuccess = (newChat: Chat) => {
            console.log('[Socket Event] Received whatsapp_add_chat_success:', newChat);
            setIsAddingChat(false);
            setIsAddChatModalOpen(false);
            toast.success(`Чат с ${newChat.name || newChat.id} успешно добавлен!`);

            // Add the chat to the list and make it active
            addChatIfNotExists(newChat, setChats, fetchClientInfo); // Use existing helper
            // Find the enriched version after it's added/updated by fetchClientInfo effect
            // Need a slight delay or a way to wait for the enrichment
            setTimeout(() => {
                setChats(currentChats => {
                    const addedChat = currentChats.find(c => c.id === newChat.id);
                    if (addedChat) {
                        handleConversationClick(addedChat as EnrichedChat); // Activate the newly added chat
                    }
                    return currentChats; // Return potentially updated chats
                });
            }, 500); // Delay to allow client info fetching/state update

        };

        const handleAddChatError = (errorMessage: string) => {
            console.error('[Socket Event] Received whatsapp_add_chat_error:', errorMessage);
            setIsAddingChat(false);
            // Keep modal open to show error
            toast.error(`Ошибка добавления чата: ${errorMessage}`);
        };

        socket.on('whatsapp_add_chat_success', handleAddChatSuccess);
        socket.on('whatsapp_add_chat_error', handleAddChatError);

        return () => {
            if (socket) {
                socket.off('whatsapp_add_chat_success', handleAddChatSuccess);
                socket.off('whatsapp_add_chat_error', handleAddChatError);
            }
        };
        // Add handleConversationClick to dependencies
    }, [socket, handleConversationClick]);


    // Effect to handle receiving media data after requesting it
    useEffect(() => {
        if (!socket) return;

        const handleMediaData = (data: { messageId: string; mimetype?: string; filename?: string; mediaData?: string; error?: string }) => {
            // Log the raw data received
            console.log(`[Socket Event] Received whatsapp_message_media_data:`, data);

            setMessages(prevMessages => {
                const newMessagesState = { ...prevMessages };
                let messageUpdated = false;

                // Same WhatsApp message can be stored under both @lid and resolved @c.us chat ids.
                // Update every matching copy so the currently open chat re-renders too.
                for (const chatId in newMessagesState) {
                    const messageIndex = newMessagesState[chatId].findIndex(msg => msg.id === data.messageId);
                    if (messageIndex !== -1) {
                        const updatedMessage = {
                            ...newMessagesState[chatId][messageIndex],
                            mediaData: data.mediaData || undefined, // Update media data
                            mimetype: data.mimetype || newMessagesState[chatId][messageIndex].mimetype, // Update mimetype if provided
                            filename: data.filename || newMessagesState[chatId][messageIndex].filename, // Update filename if provided
                            mediaRequiresDownload: Boolean(data.error), // Keep retry available when download failed
                            error: data.error || undefined // Store error if download failed
                        };
                        const updatedChatMessages = [...newMessagesState[chatId]];
                        updatedChatMessages[messageIndex] = updatedMessage;
                        newMessagesState[chatId] = updatedChatMessages;
                        messageUpdated = true;
                        console.log(`[State Update] Updated media data for message ${data.messageId} in chat ${chatId}. Error: ${data.error}`);
                    }
                }

                return messageUpdated ? newMessagesState : prevMessages;
            });
        };

        socket.on('whatsapp_message_media_data', handleMediaData);

        return () => {
            if (socket) {
                socket.off('whatsapp_message_media_data', handleMediaData);
            }
        };
    }, [socket]); // Re-run if socket instance changes


    // Function to request media download from backend
    const handleRequestMedia = useCallback((messageId: string) => {
        console.log(`[ChatWindow] handleRequestMedia called for message ID: ${messageId}`);
        
        if (!socketRef.current || !socketRef.current.connected) {
            console.error("[ChatWindow] Cannot request media, socket not connected.");
            toast.error("Нет соединения с сервером для загрузки медиа.");
            return;
        }

        let requestedMessageMeta: { filename?: string; mimetype?: string; body?: string } = {};
        
        setMessages(prevMessages => {
            const newMessagesState = { ...prevMessages };
            for (const chatId in newMessagesState) {
                const messageIndex = newMessagesState[chatId].findIndex(msg => msg.id === messageId);
                if (messageIndex !== -1) {
                    const requestedMessage = newMessagesState[chatId][messageIndex];
                    requestedMessageMeta = {
                        filename: requestedMessage.filename,
                        mimetype: requestedMessage.mimetype,
                        body: requestedMessage.message
                    };
                }
                if (messageIndex !== -1 && newMessagesState[chatId][messageIndex].mediaRequiresDownload === false) {
                    console.log(`[ChatWindow] Media for message ${messageId} is already loaded or loading.`);
                    return prevMessages;
                }
            }
            return prevMessages;
        });
        
        const chatId = activeChatRef.current?.id;

        console.log(`[ChatWindow] Emitting 'whatsapp_get_message_media' for message ID: ${messageId}${chatId ? ` in chat ${chatId}` : ''}`);
        socketRef.current.emit('whatsapp_get_message_media', { messageId, chatId, ...requestedMessageMeta });
        // The loading state is handled within MediaPlaceholder
    }, []); // No dependencies needed as socketRef is stable

    const stripHtml = (html: string) => {
        let doc = new DOMParser().parseFromString(html, 'text/html');
        return doc.body.textContent || "";
    }

    const getConnectedSendSocket = (): Socket | null => {
        const currentSocket = socketRef.current;
        if (!currentSocket || !currentSocket.connected) {
            console.error("Cannot send message: socket is not connected.");
            setError(SEND_SOCKET_DISCONNECTED_ERROR);
            toast.error(SEND_SOCKET_DISCONNECTED_ERROR);
            return null;
        }

        return currentSocket;
    };

    // Unified message sending handler (accepts SendMessagePayload)
    const handleSendMessage = (payload: SendMessagePayload) => {
        if (!activeChat) {
            console.error("Cannot send message: activeChat missing.");
            // Optionally show an error to the user
            setError("Не удалось отправить сообщение: нет активного чата.");
            return;
        }

        const currentSocket = getConnectedSendSocket();
        if (!currentSocket) return;

        // Basic validation
        if (!payload.media && (!payload.message || !payload.message.trim())) {
            console.warn("Attempted to send empty message.");
            return; // Don't send empty text messages
        }

        // Clean text message if present
        if (payload.message) {
            payload.message = stripHtml(payload.message.trim().replace(/&.+;/g, ''));
        }

        console.log(`Sending message payload to ${activeChat.name} (${activeChat.id}):`, {
            ...payload,
            media: payload.media ? (Array.isArray(payload.media) ? `[${payload.media.length} files]` : '{...}') : null
        });

        // Add specific options for voice messages if needed
        if (payload.media && !Array.isArray(payload.media) && payload.media.mimetype.startsWith('audio/')) {
            payload.options = { ...(payload.options || {}), sendAudioAsVoice: true };
            console.log("Setting sendAudioAsVoice option for audio message.");
        }

        const sendPayload: SendMessagePayload = {
            ...payload,
            phone: payload.phone || activeChat.phone
        };

        const clientMessageId = `temp-${Date.now()}`;

        // --- Optimistic UI Update ---
        const originalMessage = payload.quotedMessageId ? getActiveChatMessages().find(m => m.id === payload.quotedMessageId) : undefined;

        let tempOutgoingMessage: WhatsAppMessage;

        if (payload.media) {
            if (!Array.isArray(payload.media)) {
                tempOutgoingMessage = {
                    id: clientMessageId,
                    message: payload.message || '',
                    sender: 'Me',
                    direction: 'outgoing',
                    position: 'single',
                    chatId: activeChat.id,
                    timestamp: Date.now(),
                    type: getMessageTypeFromMimeType(payload.media.mimetype),
                    hasMedia: true,
                    mimetype: payload.media.mimetype,
                    filename: payload.media.filename,
                    mediaData: payload.media.data,
                    ack: 0,
                    sendStatus: 'pending',
                    hasQuotedMsg: !!payload.quotedMessageId,
                    quotedMessage: originalMessage,
                };
            } else {
                const baseTimestamp = Date.now();
                payload.media.forEach((mediaItem, index) => {
                    const multiClientMessageId = `temp-${baseTimestamp}-${index}`;
                    const multiMessage = index === 0 ? payload.message || '' : '';
                    const multiOptions = mediaItem.mimetype.startsWith('audio/')
                        ? { ...(sendPayload.options || {}), sendAudioAsVoice: true }
                        : sendPayload.options;
                    const tempMultiMessage: WhatsAppMessage = {
                        id: multiClientMessageId,
                        message: multiMessage,
                        sender: 'Me',
                        direction: 'outgoing',
                        position: 'single',
                        chatId: activeChat.id,
                        timestamp: baseTimestamp + index,
                        type: getMessageTypeFromMimeType(mediaItem.mimetype),
                        hasMedia: true,
                        mimetype: mediaItem.mimetype,
                        filename: mediaItem.filename,
                        mediaData: mediaItem.data,
                        ack: 0,
                        sendStatus: 'pending',
                        hasQuotedMsg: !!payload.quotedMessageId,
                        quotedMessage: originalMessage,
                    };

                    setMessages(prev => ({
                        ...prev,
                        [activeChat.id]: [...(prev[activeChat.id] || []), tempMultiMessage]
                    }));

                    currentSocket.emit('whatsapp_send_message', {
                        ...sendPayload,
                        message: multiMessage,
                        media: mediaItem,
                        options: multiOptions,
                        clientMessageId: multiClientMessageId
                    });
                });
                
                return;
            }
        } else {
            // Optimistic update for text message
            tempOutgoingMessage = {
                id: clientMessageId,
                message: payload.message,
                sender: 'Me',
                direction: 'outgoing',
                position: 'single',
                chatId: activeChat.id,
                timestamp: Date.now(),
                type: 'chat',
                hasMedia: false,
                ack: 0,
                sendStatus: 'pending',
                hasQuotedMsg: !!payload.quotedMessageId,
                quotedMessage: originalMessage,
            };
        }

        setMessages(prev => ({
            ...prev,
            [activeChat.id]: [...(prev[activeChat.id] || []), tempOutgoingMessage]
        }));
        currentSocket.emit('whatsapp_send_message', {
            ...sendPayload,
            clientMessageId
        });
        // Input value clearing is handled within ChatWindow
    };

    // Remove handleReplyToMessage - moved to ChatWindow

    // Trigger hidden file input
    const handleAttachClick = () => { fileInputRef.current?.click(); };

    const getMessageTypeFromMimeType = (mimetype: string): string => {
        if (mimetype.startsWith('image/')) return 'image';
        if (mimetype.startsWith('video/')) return 'video';
        if (mimetype.startsWith('audio/')) return 'audio';
        return 'document';
    };

    // Handle file selection and sending (now accepts quotedMessageId from ChatWindow)
    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>, quotedMessageId?: string) => {
        if (!event.target.files || event.target.files.length === 0 || !activeChat) return;
        if (!getConnectedSendSocket()) return;
        
        const files = Array.from(event.target.files);
        console.log(`📁 Selected ${files.length} files:`, files.map(f => ({ name: f.name, type: f.type, size: f.size })));
        
        files.forEach((file, index) => {
            const reader = new FileReader();
            reader.onload = (loadEvent) => {
                const base64String = loadEvent.target?.result as string;
                if (!base64String) { 
                    console.error(`Failed to read file: ${file.name}`);
                    return; 
                }
                
                const match = base64String.match(/^data:(.+);base64,(.+)$/);
                if (!match) { 
                    console.error(`Invalid file data format for: ${file.name}`);
                    return; 
                }
                
                const mimetype = match[1]; 
                const base64Data = match[2]; 
                const filename = file.name; 
                const caption = index === 0 ? messageInputValue.trim() : ''; 
                
                const mediaToSend: MediaToSend = { mimetype, data: base64Data, filename };

                const clientMessageId = `temp-${Date.now()}-${index}`;
                const sendSocket = getConnectedSendSocket();
                if (!sendSocket) return;

                const payload: SendMessagePayload = {
                    chatId: activeChat.id,
                    clientMessageId,
                    phone: activeChat.phone,
                    message: caption,
                    media: mediaToSend,
                    quotedMessageId: quotedMessageId
                };

                console.log(`📤 Sending file ${index + 1}/${files.length}: ${filename} to ${activeChat.name}`);
                
                if (quotedMessageId) {
                    console.log(`↩️ Sending media reply to message ${quotedMessageId}`);
                }

                const originalMessage = quotedMessageId ? getActiveChatMessages().find(m => m.id === quotedMessageId) : undefined;

                const tempOutgoingMediaMessage: WhatsAppMessage = {
                    id: clientMessageId,
                    message: caption,
                    sender: 'Me',
                    direction: 'outgoing',
                    position: 'single',
                    chatId: activeChat.id,
                    timestamp: Date.now(),
                    type: getMessageTypeFromMimeType(mimetype),
                    hasMedia: true,
                    mimetype: mimetype,
                    filename: filename,
                    mediaData: base64Data,
                    ack: 0,
                    sendStatus: 'pending',
                    hasQuotedMsg: !!quotedMessageId,
                    quotedMessage: originalMessage,
                };
                
                setMessages(prev => ({ 
                    ...prev, 
                    [activeChat.id]: [...(prev[activeChat.id] || []), tempOutgoingMediaMessage] 
                }));
                sendSocket.emit('whatsapp_send_message', payload);
            };
            
            reader.onerror = () => { 
                console.error(`Error reading file: ${file.name}`);
                setError(`Ошибка чтения файла: ${file.name}`);
            };
            
            reader.readAsDataURL(file);
        });
        
        setMessageInputValue("");
        event.target.value = ''; // Reset input
    };

    // Get messages for the active chat
    const getActiveChatMessages = (): WhatsAppMessage[] => activeChat
        ? getMessagesForChat(messages, activeChat as EnrichedChat)
        : [];

    // Handle loading more message history by increasing the limit
    const handleLoadHistory = useCallback(() => {
        if (!socketRef.current || !socketRef.current.connected || !activeChat || isLoadingHistory) {
            console.log('[Load History] Aborted. Conditions not met:', { socket: !!socketRef.current?.connected, activeChat: !!activeChat, isLoadingHistory });
            return;
        }

        const currentMessages = getMessagesForChat(messages, activeChat as EnrichedChat);
        const currentLimit = currentMessages.length;
        const increaseAmount = DEFAULT_HISTORY_MESSAGES_LIMIT; // How many more messages to fetch each time
        const newLimit = currentLimit + increaseAmount;

        console.log(`[Load History] Requesting more history for ${activeChat.id}. Current count: ${currentLimit}, New limit: ${newLimit}`);
        setIsLoadingHistory(true);
        socketRef.current.emit('whatsapp_get_chat_history', getChatHistoryRequest(activeChat as EnrichedChat, newLimit));

    }, [activeChat, messages, isLoadingHistory]); // Dependencies

    const handleCallContact = (phone: string) => {
        console.log(`Calling contact: ${phone}`);
        window.open(`tel:${phone}`, '_blank');
    };

    const handleMessageContact = (phone: string) => {
        console.log(`Starting new chat with: ${phone}`);
        toast.info(`Функция создания чата с ${phone} будет реализована`);
    };

    const handleEmailContact = (email: string) => {
        console.log(`Emailing: ${email}`);
        window.open(`mailto:${email}`, '_blank');
    };

    const handleSaveContact = (contact: VCardContact) => {
        console.log('Saving contact:', contact);
        const vcard = createVCard(contact);
        const blob = new Blob([vcard], { type: 'text/vcard' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${contact.name || 'contact'}.vcf`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        toast.success(`Контакт ${contact.name || ''} сохранен как vCard файл`);
    };

    const handleViewContact = (contact: VCardContact) => {
        console.log('Viewing contact details:', contact);
        const details = [
            contact.name && `Имя: ${contact.name}`,
            contact.phone && `Телефон: ${formatPhoneNumber(contact.phone)}`,
            contact.email && `Email: ${contact.email}`,
            contact.org && `Организация: ${contact.org}`,
            contact.title && `Должность: ${contact.title}`
        ].filter(Boolean).join('\n');
        
        alert(details || 'Нет информации о контакте');
    };

    const handleCreateChat = (phoneNumber: string, name?: string) => {
        console.log('🚀 WhatsAppPage: handleCreateChat called with:', { phoneNumber, name });
        
        if (!socketRef.current || !socketRef.current.connected) {
            toast.error("Нет соединения с сервером для создания чата.");
            return;
        }

        let cleanedPhone = phoneNumber.replace(/\D/g, '');
        
        if (cleanedPhone.startsWith('7') && cleanedPhone.length === 11) {
            cleanedPhone = '7' + cleanedPhone.substring(1);
        } else if (cleanedPhone.startsWith('8') && cleanedPhone.length === 11) {
            cleanedPhone = '7' + cleanedPhone.substring(1);
        }
        
        console.log('📞 Final phone number to send:', cleanedPhone);

        if (!cleanedPhone || cleanedPhone.length < 10) {
            toast.error(`Неверный номер телефона: ${cleanedPhone}`);
            return;
        }

        console.log(`[Create Chat] Emitting whatsapp_add_chat_by_phone for ${cleanedPhone}`);
        socketRef.current.emit('whatsapp_add_chat_by_phone', cleanedPhone);
        
        toast.info(`Создание чата с ${name || cleanedPhone}...`);
    };


    // Function to load all history for a chat and then scroll to a message
    const handleLoadAllHistoryAndScroll = useCallback((messageId: string) => {
        if (!socketRef.current || !socketRef.current.connected || !activeChat) {
            toast.error("Невозможно загрузить историю: нет активного чата или соединения.");
            return;
        }
        console.log(`[Load All History & Scroll] Requesting ALL history for chat ${activeChat.id} to find message ${messageId}.`);
        setIsLoadingHistory(true);
        setIsLoadingAllHistoryForScroll(true);
        setScrollToMessageId(messageId); // Set the target message ID
        // Emit event to backend to get ALL messages. Assuming limit: -1 or a very large number means all.
        // Adjust if your backend has a different way to request all messages.
        socketRef.current.emit('whatsapp_get_chat_history', getChatHistoryRequest(activeChat as EnrichedChat, 999999)); // Or a specific "all" flag
    }, [activeChat]);

    // Callback for ChatWindow to notify when scrolling to a quoted message is done
    const handleQuotedMessageScrolled = useCallback(() => {
        console.log("[WhatsAppPage] ChatWindow has scrolled to the message. Resetting scrollToMessageId.");
        setScrollToMessageId(null);
        // setIsLoadingAllHistoryForScroll(false); // Also reset this if it was set
    }, []);


    // --- Render Logic ---

    // Display error message prominently if it exists (and isn't just a connection issue handled by ConnectionStatus)
    if (error && !error.toLowerCase().includes('ошибка подключения') && !error.toLowerCase().includes('отключено')) {
        // Keep generic error prefix for now, or translate if needed
        return <div className="p-4 text-red-600 bg-red-100 border border-red-400 rounded">Ошибка: {error}</div>;
    }

    // Show QR code if available
    if (qrCode) {
        return <QRCodeDisplay qrCode={qrCode} />;
    }

    // Show connecting/authenticating status if not connected or authenticating
    if (!isConnected || isAuthenticating) {
        return <ConnectionStatus isAuthenticating={isAuthenticating} isConnected={isConnected} error={error} />;
    }

    // Main chat interface
    return (
        <div className="flex h-dvh max-h-dvh overflow-hidden relative"> {/* Use full screen height and hide overflow, add relative */}
            <ToastContainer
                position="top-right"
                autoClose={5000}
                hideProgressBar={false}
                newestOnTop={false}
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
                theme="light" // or "dark" or "colored"
            />
            {/* Sidebar Container: Controls visibility and positioning */}
            <div className={`
                ${isMobileSidebarVisible ? 'flex absolute top-0 left-0 w-full h-dvh z-10' : 'hidden'}
                ${isCollapsed ? 'md:w-0' : 'md:static md:h-dvh md:z-auto md:w-1/3 lg:w-1/4'}
                md:flex               
                bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 
                flex-col  /* Ensure flex column for sidebar content */
            `}>
                <ChatSidebar
                    chats={chats} // EnrichedChat[]
                    activeChatId={activeChat?.id || null}
                    onConversationClick={handleConversationClick}
                    onAddChatClick={() => setIsAddChatModalOpen(true)}
                    isCollapsed={isCollapsed}
                    toggleSidebar={toggleSidebar}
                />
            </div>

            {/* Chat Window Container: Controls visibility and positioning */}
            <div className={`
                ${!isMobileSidebarVisible && activeChat ? 'flex absolute top-0 left-0 w-full h-dvh z-0' : 'hidden'}  
                md:static md:flex-1 md:h-dvh flex-col
                md:flex w-full
                 /* Ensure flex column for chat window content */
            `}>
                {activeChat ? (
                    <> {/* Wrap in fragment to include ReplyPreview */}
                        {/* Reply Preview is now rendered inside ChatWindow */}
                        <ChatWindow
                            activeChat={activeChat as EnrichedChat}
                            messages={getActiveChatMessages()}
                            isMobileSidebarVisible={isMobileSidebarVisible} // Pass state
                            toggleMobileSidebarVisibility={toggleMobileSidebarVisibility} // Pass function
                            isLoadingHistory={isLoadingHistory}
                            presenceStatus={activeChatPresence}
                            lastSeen={activeChatLastSeen}
                            activeClientStatsMap={activeClientStatsMap} // Pass stats map
                            isLoadingStats={isLoadingStats} // Pass loading state for stats
                            messageInputValueParent={messageInputValue}
                            onMessageChangeParent={setMessageInputValue}
                            onSendMessage={handleSendMessage}
                            onAttachClick={handleAttachClick}
                            fileInputRef={fileInputRef}
                            onFileChange={handleFileChange}
                            onLoadHistory={handleLoadHistory}
                            onRequestMedia={handleRequestMedia}
                            chats={chats} // Pass the chats state here
                            onLoadAllHistoryAndScroll={handleLoadAllHistoryAndScroll} // Pass new handler
                            scrollToMessageId={scrollToMessageId} // Pass scroll target
                            onQuotedMessageScrolled={handleQuotedMessageScrolled} // Pass reset callback
                            onCallContact={handleCallContact}
                            onMessageContact={handleMessageContact}
                            onEmailContact={handleEmailContact}
                            onSaveContact={handleSaveContact}
                            onViewContact={handleViewContact}
                            onCreateChat={handleCreateChat}
                        // Remove onReplyToMessage prop
                        />
                    </>
                ) : (
                    // Placeholder when no chat is active (visible on larger screens)
                    <div className="hidden md:flex flex-1 items-center justify-center h-dvh w-full text-gray-500 bg-gray-50 dark:bg-gray-900 dark:text-gray-400">
                        Выберите чат, чтобы начать общение
                    </div>
                )}
            </div>

            {/* Add Chat Modal */}
            <AddChatModal
                isOpen={isAddChatModalOpen}
                onClose={() => setIsAddChatModalOpen(false)}
                isLoading={isAddingChat}
                onAddChat={(phoneNumber) => {
                    if (!socketRef.current || !socketRef.current.connected) {
                        toast.error("Нет соединения с сервером для добавления чата.");
                        return;
                    }
                    console.log(`[Add Chat] Emitting whatsapp_add_chat_by_phone for ${phoneNumber}`);
                    setIsAddingChat(true);
                    socketRef.current.emit('whatsapp_add_chat_by_phone', phoneNumber);
                }}
            />
        </div>
    );
};

const extractPhoneFromChat = (chat: Chat): string | undefined => {
    const cUsMatch = chat.id.match(/^(\d+)@c\.us$/);
    if (cUsMatch) {
        return cUsMatch[1];
    }

    // A numeric @lid value is an internal WhatsApp identifier, not a phone.
    // For those chats only trust the explicit `phone` resolved by the backend.
    return undefined;
};


const normalizePhoneForApi = (phone: string): string => {
    const digits = phone.replace(/\D/g, '');
    
    if (digits.startsWith('7') && digits.length === 11) {
        return digits;
    } else if (digits.length === 10) {
        return '7' + digits;
    } else if (digits.startsWith('8') && digits.length === 11) {
        return '7' + digits.substring(1);
    }
    
    return digits;
};

const getUniqueStrings = (...values: Array<string | undefined | null>): string[] => (
    Array.from(new Set(values.filter((value): value is string => Boolean(value && value.trim()))))
);

const getPhoneChatId = (phone?: string): string | undefined => {
    if (!phone) return undefined;

    const normalizedPhone = normalizePhoneForApi(phone);
    return normalizedPhone ? `${normalizedPhone}@c.us` : undefined;
};

const getNormalizedChatPhone = (chat?: Partial<Chat | EnrichedChat> | null): string | undefined => {
    const explicitPhone = (chat as EnrichedChat | undefined)?.phone;
    const extractedPhone = chat?.id ? extractPhoneFromChat(chat as Chat) : undefined;
    const phone = explicitPhone || extractedPhone;

    return phone ? normalizePhoneForApi(phone) : undefined;
};

const getChatMessageKeys = (
    chat?: Partial<Chat | EnrichedChat> | null,
    extraIds: Array<string | undefined | null> = []
): string[] => {
    const phone = getNormalizedChatPhone(chat);
    return getUniqueStrings(
        chat?.id,
        ...(chat?.aliases || []),
        getPhoneChatId(phone),
        ...extraIds
    );
};

const getMessagesForChat = (
    messagesByChatId: { [chatId: string]: WhatsAppMessage[] },
    chat: Chat | EnrichedChat
): WhatsAppMessage[] => {
    const aliases = getChatMessageKeys(chat);
    const uniqueMessages = new Map<string, WhatsAppMessage>();

    aliases.flatMap(alias => messagesByChatId[alias] || []).forEach(message => {
        if (!uniqueMessages.has(message.id)) {
            uniqueMessages.set(message.id, message);
        }
    });

    return Array.from(uniqueMessages.values()).sort((a, b) => a.timestamp - b.timestamp);
};

const getChatHistoryRequest = (
    chat: Chat | EnrichedChat,
    limit: number
): { chatId: string; chatIds: string[]; limit: number; phone?: string } => ({
    chatId: chat.id,
    chatIds: getChatMessageKeys(chat),
    limit,
    phone: getNormalizedChatPhone(chat)
});

const getChatDedupKey = (chat: Chat | EnrichedChat): string => {
    const phone = (chat as EnrichedChat).phone || extractPhoneFromChat(chat);
    const normalizedPhone = phone ? normalizePhoneForApi(phone) : '';
    return normalizedPhone ? `phone:${normalizedPhone}` : `id:${chat.id}`;
};

const isPhoneChatId = (chatId: string): boolean => chatId.includes('@c.us');

const isUsefulChatName = (name?: string): boolean => {
    const value = String(name || '').trim();
    if (!value || value === 'Unknown' || value === 'Me') return false;
    return !/^\+?\d+$/.test(value.replace(/\D/g, ''));
};

const mergeEnrichedChats = (existing: EnrichedChat, incoming: EnrichedChat): EnrichedChat => {
    const existingTimestamp = existing.timestamp || 0;
    const incomingTimestamp = incoming.timestamp || 0;
    const latest = incomingTimestamp >= existingTimestamp ? incoming : existing;
    const preferredId = isPhoneChatId(incoming.id)
        ? incoming.id
        : (isPhoneChatId(existing.id) ? existing.id : latest.id);
    const phone = incoming.phone || existing.phone;
    const name = isUsefulChatName(incoming.name)
        ? incoming.name
        : (isUsefulChatName(existing.name) ? existing.name : latest.name);

    return {
        ...latest,
        id: preferredId,
        aliases: getUniqueStrings(
            ...(existing.aliases || []),
            ...(incoming.aliases || []),
            existing.id,
            incoming.id
        ),
        name,
        phone,
        profilePicUrl: incoming.profilePicUrl || existing.profilePicUrl || latest.profilePicUrl,
        unreadCount: Math.max(existing.unreadCount || 0, incoming.unreadCount || 0),
        clientId: incoming.clientId || existing.clientId,
        clientIds: incoming.clientIds || existing.clientIds,
        clientNames: incoming.clientNames || existing.clientNames,
        isKnownClient: incoming.isKnownClient || existing.isKnownClient,
        contactName: incoming.contactName || existing.contactName
    };
};

const deduplicateEnrichedChats = (chatList: EnrichedChat[]): EnrichedChat[] => {
    const chatsByKey = new Map<string, EnrichedChat>();

    chatList.forEach(chat => {
        const key = getChatDedupKey(chat);
        const existing = chatsByKey.get(key);
        chatsByKey.set(key, existing ? mergeEnrichedChats(existing, chat) : chat);
    });

    return Array.from(chatsByKey.values());
};


// const formatPhoneForDisplay = (input: string): string => {
//     const digits = input.replace(/\D/g, '');
    
//     if (digits.length >= 10 && digits.length <= 12) {
//         return formatPhoneNumber(digits);
//     }
    
//     return input;
// };

// --- Helper Functions (Ensure they are outside the component) ---

function addAllChats(chatList: Chat[], setChats: React.Dispatch<React.SetStateAction<EnrichedChat[]>>, fetchClientInfo: (phoneNumbers: string[]) => Promise<void>) {
    console.log('Received raw chat list:', chatList);
    const phoneNumbersToFetch: string[] = [];
    
    const enrichedChats = chatList.map(chat => {
        let phone = (chat as EnrichedChat).phone || extractPhoneFromChat(chat);
        let displayName = chat.name;
        const normalizedPhone = phone ? normalizePhoneForApi(phone) : '';
        if (normalizedPhone) {
            phone = normalizedPhone;
        }
        
        if (chat.name && phone) {
            const cleanedName = chat.name
                .replace(new RegExp(`\\s*\\(?\\+?${phone}\\)?\\s*`, 'g'), '')
                .replace(new RegExp(`\\s*${formatPhoneNumber(phone)}\\s*`, 'g'), '')
                .trim();
            
            if (cleanedName && cleanedName !== chat.name) {
                displayName = cleanedName;
            }
            
            if (!displayName || /^\d+$/.test(displayName.replace(/\D/g, ''))) {
                displayName = formatPhoneNumber(phone);
            }
        }
        
        if (normalizedPhone) {
            phoneNumbersToFetch.push(normalizedPhone);
        }
        
        return {
            ...chat,
            name: displayName,
            phone: phone,
            clientId: undefined,
            isKnownClient: false,
            contactName: undefined,
            clientNames: undefined
        } as EnrichedChat;
    });

    setChats(deduplicateEnrichedChats(enrichedChats));

    if (phoneNumbersToFetch.length > 0) {
        fetchClientInfo(phoneNumbersToFetch);
    }
}

function addChatIfNotExists(chat: Chat, setChats: React.Dispatch<React.SetStateAction<EnrichedChat[]>>, fetchClientInfo: (phoneNumbers: string[]) => Promise<void>) {
    console.log('Received raw chat:', chat);
    
    let phone = (chat as any).phone || extractPhoneFromChat(chat);
    const normalizedPhone = phone ? normalizePhoneForApi(phone) : '';
    if (normalizedPhone) {
        phone = normalizedPhone;
    }
    let displayName = chat.name;
    
    // Don't override the name if it's already set and not "Me"
    if (chat.name && phone && chat.name !== 'Me') {
        const cleanedName = chat.name
            .replace(new RegExp(`\\s*\\(?\\+?${phone}\\)?\\s*`, 'g'), '')
            .replace(new RegExp(`\\s*${formatPhoneNumber(phone)}\\s*`, 'g'), '')
            .trim();
        
        if (cleanedName && cleanedName !== chat.name) {
            displayName = cleanedName;
        }
        
        if (!displayName || /^\d+$/.test(displayName.replace(/\D/g, ''))) {
            displayName = formatPhoneNumber(phone);
        }
    } else if (chat.name === 'Me') {
        // If name is "Me", try to extract from phone number
        if (phone) {
            displayName = formatPhoneNumber(phone);
        }
    }
    
    const phoneNumbersToFetch: string[] = [];

    if (normalizedPhone) {
        console.log(`📞 Extracted phone: ${phone}`);
        
        phoneNumbersToFetch.push(normalizedPhone);
    }

    setChats(prevChats => {
        // Match by id OR by phone number to prevent duplicates across @lid/@c.us
        const incomingDedupKey = getChatDedupKey({ ...chat, phone } as EnrichedChat);
        const existingChatIndex = prevChats.findIndex(c => 
            c.id === chat.id || 
            getChatDedupKey(c) === incomingDedupKey
        );
        
        if (existingChatIndex === -1) {
            const enrichedChat: EnrichedChat = {
                ...chat,
                name: displayName,
                phone: phone,
                clientId: undefined,
                isKnownClient: false,
                contactName: undefined,
                clientNames: undefined
            };
            return [enrichedChat, ...prevChats];
        } else {
            // Update existing chat but preserve enriched data and prefer the better ID (@c.us over @lid)
            const existingChat = prevChats[existingChatIndex];
            const preferredId = chat.id.includes('@c.us') ? chat.id : existingChat.id;
            return prevChats.map((c, index) => 
                index === existingChatIndex 
                    ? { 
                        ...c,
                        ...chat,
                        id: preferredId,
                        name: existingChat.isKnownClient ? existingChat.name : (displayName || existingChat.name),
                        phone: phone || existingChat.phone,
                        clientId: existingChat.clientId,
                        isKnownClient: existingChat.isKnownClient,
                        contactName: existingChat.contactName,
                        clientNames: existingChat.clientNames
                    }
                    : c
            );
        }
    });

    if (phoneNumbersToFetch.length > 0) {
        fetchClientInfo(phoneNumbersToFetch);
    }
}

function convertMessageData(msgData: any, data: { chatId: string; messages: any[]; }): WhatsAppMessage {
  let parsedVCard: VCardContact | undefined;
  const timestampMs = msgData.timestamp * 1000;
  
  if (msgData.vcard) {
    console.log('🔄 convertMessageData: Processing vcard:', {
      vcardType: typeof msgData.vcard,
      vcardData: msgData.vcard
    });
    
    if (typeof msgData.vcard === 'string') {
      parsedVCard = parseVCard(msgData.vcard);
      console.log('✅ convertMessageData: Parsed vCard string:', parsedVCard);
    } else if (typeof msgData.vcard === 'object' && msgData.vcard !== null) {
      parsedVCard = msgData.vcard;
      console.log('✅ convertMessageData: Using vCard object:', parsedVCard);
    }
  }

  return {
    id: msgData.id,
    message: msgData.body || '',
    sender: msgData.senderName,
    direction: msgData.fromMe ? 'outgoing' : 'incoming',
    position: 'normal',
    chatId: data.chatId,
    timestamp: timestampMs,
    profilePicUrl: msgData.profilePicUrl,
    type: msgData.type,
    hasMedia: msgData.hasMedia,
    mimetype: msgData.mimetype,
    filename: msgData.filename,
    mediaData: msgData.mediaData,
    error: msgData.error,
    location: msgData.location,
    vcard: msgData.vcard,
    parsedVCard: parsedVCard,
    ack: msgData.ack,
    mediaRequiresDownload: msgData.mediaRequiresDownload,
    hasQuotedMsg: msgData.hasQuotedMsg,
    quotedMessage: msgData.quotedMessage ? convertMessageData(msgData.quotedMessage, data) : msgData.quotedMessage,
    hasReaction: msgData.hasReaction,
    reactions: msgData.reactions,
    historySource: msgData.historySource,
    isArchivedOnly: msgData.isArchivedOnly,
    ...getRecoveredSendState(msgData, timestampMs),
  };
}


export default WhatsAppPage;
